/**
 * A banner's fill: the same colour, with depth.
 *
 * Every global banner used to be one flat fill. Flat reads as unfinished at this
 * size — a full-width strip of a single strong colour is the largest block on
 * the page and has nothing happening in it. But a banner's colour is doing a
 * job: red means something is wrong, amber means attention, green means it went
 * well. So the shift has to stay inside the colour, not travel across hues.
 *
 * Three stops, lifted then dropped around the original: lighter at the start,
 * the true colour in the middle, deeper at the end.
 *
 * The move is made in lightness, holding the hue, and NOT by mixing toward
 * white and black. Mixing toward white raises every channel by the same
 * fraction of its distance to 255, which on a saturated colour lifts the two
 * small channels far more than the dominant one — so a strong red drifts pink
 * as it lightens. Lightness moves the colour along its own ramp and keeps it
 * recognisably itself.
 *
 * Saturation rises slightly at the light end for the same reason: lightening
 * alone flattens a colour toward grey, and a few points back makes the light
 * end read as the colour rather than as a faded version of it.
 *
 * Angle is shallow (100°) so the change runs the length of a wide strip rather
 * than across its height, where a 40px-tall banner would show it as a band.
 */

type Rgb = { r: number; g: number; b: number; a: number };
type Hsl = { h: number; s: number; l: number; a: number };

const parse = (colour: string): Rgb | null => {
  const hex = colour.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].replace(/./g, (c) => c + c) : hex[1];
    return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16), a: 1 };
  }
  // Alpha is carried through, not dropped. Several banners are a 6–10% tint of
  // their colour rather than a solid fill; turning one of those opaque would
  // not be adding depth to it, it would be replacing it with a different
  // component.
  const rgb = colour.trim().match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:[\s,/]+([\d.]+))?/i);
  if (rgb) return { r: +rgb[1], g: +rgb[2], b: +rgb[3], a: rgb[4] === undefined ? 1 : +rgb[4] };
  return null;
};

const toHsl = ({ r, g, b, a }: Rgb): Hsl => {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  const d = max - min;
  const l = (max + min) / 2;
  if (d === 0) return { h: 0, s: 0, l, a };
  const s = d / (1 - Math.abs(2 * l - 1));
  const h =
    max === R ? 60 * (((G - B) / d + 6) % 6)
    : max === G ? 60 * ((B - R) / d + 2)
    : 60 * ((R - G) / d + 4);
  return { h, s, l, a };
};

const toRgb = ({ h, s, l, a }: Hsl): string => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [R, G, B] =
    h < 60 ? [c, x, 0]
    : h < 120 ? [x, c, 0]
    : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c]
    : h < 300 ? [x, 0, c]
    : [c, 0, x];
  const to255 = (v: number) => Math.max(0, Math.min(255, Math.round((v + m) * 255)));
  return a >= 1
    ? `rgb(${to255(R)}, ${to255(G)}, ${to255(B)})`
    : `rgba(${to255(R)}, ${to255(G)}, ${to255(B)}, ${a})`;
};

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/**
 * A tint has no lightness to move.
 *
 * On a solid fill, depth comes from lightness: lighter at one end, deeper at
 * the other. On a 10% tint that does nothing you can see — the colour is 90%
 * whatever is behind it, so shifting the 10% by a few points is invisible, and
 * the banner reads as flat no matter what the gradient says.
 *
 * What is actually varying on a tint is how much of it there is. So below full
 * opacity the ends move the ALPHA instead, which is the same idea expressed in
 * the only channel that is doing any work.
 */
const TRANSLUCENT_LIFT = 2.8;
const TRANSLUCENT_DROP = 0.3;

/** Lighter, and a touch more saturated so it does not read as washed out. */
const lighter = (base: Hsl): string =>
  base.a < 1
    ? toRgb({ h: base.h, s: base.s, l: clamp01(base.l + 0.02), a: clamp01(base.a * TRANSLUCENT_LIFT) })
    : toRgb({ h: base.h, s: clamp01(base.s + 0.06), l: clamp01(base.l + 0.053), a: base.a });

/** Deeper. Saturation is left alone — a dark end holds its colour on its own. */
const deeper = (base: Hsl): string =>
  base.a < 1
    ? toRgb({ h: base.h, s: base.s, l: clamp01(base.l - 0.02), a: clamp01(base.a * TRANSLUCENT_DROP) })
    : toRgb({ h: base.h, s: base.s, l: clamp01(base.l - 0.068), a: base.a });

/**
 * `bannerGradient("rgb(195, 75, 54)")` → a gradient that still reads as that red.
 * Anything unparseable is returned untouched, so a caller can pass a gradient or
 * a keyword and get its own value back rather than a broken one.
 */
/** The red was chosen by eye first, and the formula above was fitted to it. It
    lands one point of green away, which is invisible — but the red is the
    banner seen most, so it is pinned to the exact value rather than to a value
    that merely rounds to it. */
const CHOSEN: Record<string, string> = {
  "rgb(195, 75, 54)":
    "linear-gradient(100deg, rgb(211, 86, 65) 0%, rgb(195, 75, 54) 45%, rgb(168, 65, 46) 100%)",
};

export const bannerGradient = (colour: string): string => {
  const chosen = CHOSEN[colour.trim()];
  if (chosen) return chosen;
  const base = parse(colour);
  if (!base) return colour;
  const hsl = toHsl(base);
  return `linear-gradient(100deg, ${lighter(hsl)} 0%, ${colour} 45%, ${deeper(hsl)} 100%)`;
};

/**
 * How strongly the hex texture is drawn on a given fill.
 *
 * One opacity does not work across four colours. Thin white lines all but
 * vanish on the blue and shout on the red, so the weight was tuned per colour
 * by eye against the real banner rather than derived — luminance does not
 * predict it (the red and the green sit within four points of each other and
 * want 190% and 130%).
 *
 * Anything else — an incident banner carries an admin-chosen colour — gets a
 * middle value. It will not be perfect, but it will be present and quiet,
 * which is the failure worth having.
 */
const PATTERN_STRENGTH: Record<string, number> = {
  "rgb(41, 98, 155)": 1.04,  // blue   — beta
  "rgb(46, 125, 50)": 1.3,   // green  — recovered, downgrade confirmed
  "rgb(191, 117, 22)": 1.77, // amber  — attention
  "rgb(195, 75, 54)": 1.9,   // red    — something is wrong
};
const DEFAULT_STRENGTH = 1.3;
/**
 * A tint gets the texture too, but quieter.
 *
 * On a solid fill the white lines are drawn onto the colour and the colour
 * mutes them. On a 10% tint there is almost nothing to mute them — they land
 * on the page behind — so the same opacity that reads as a whisper on the red
 * reads as a printed pattern on the amber.
 */
const TRANSLUCENT_PATTERN = 0.55;

/**
 * Everything a banner needs on its own element: the fill, and the dial the
 * texture reads.
 *
 * The texture itself is two pseudo-elements in `index.css` keyed on
 * `data-banner-surface` — a bloom and a hex scatter. Putting them there rather
 * than in each component means a banner cannot be built without them, and
 * cannot drift from the others.
 */
export const bannerSurface = (colour: string): Record<string, string> => {
  const base = parse(colour);
  const fallback = base && base.a < 1 ? TRANSLUCENT_PATTERN : DEFAULT_STRENGTH;
  return {
    background: bannerGradient(colour),
    "--banner-pattern": String(PATTERN_STRENGTH[colour.trim()] ?? fallback),
  };
};
